import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectCustom from "@assessment/components/atoms/selectCustom";
import Button from "@lxp-app/components/atoms/button";
import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import { useParams } from "react-router-dom";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import {
  useStartAssessmentMutation,
  useUpdateSessionIdMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import GetHeroIcon from "@lxp-app/utils/getHeroIcon";
import { InfoComponent } from "@lxp-app/components/atoms/infoComponent";
import { generateUniqueId } from "../generateId";

interface Props {
  setActiveStep?: (val: "next" | "previous") => void;
  inResumeScreen?: boolean;
  setActiveView?: (
    val: "instructions" | "systemCheck" | "audioVideoCheck"
  ) => void;
}

const AudioVideoCheck = ({
  setActiveStep,
  setActiveView,
  inResumeScreen = false
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { scheduleId } = useParams();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);

  const [selectedCamera, setSelectedCamera] = useState("");
  const [videoURL, setVideoURL] = useState<string>("");
  const videoURLRef = useRef<string>("");
  const [selectedMic, setSelectedMic] = useState("");
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const [permissionError, setPermissionError] = useState("");
  const [isErr, setisErr] = useState<boolean>(false);

  const [startAssessment, { isLoading }] = useStartAssessmentMutation();
  const [updateTabId] = useUpdateSessionIdMutation();

  const tncEnabled = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.tncEnabled
  );

  const constants = useAppSelector((store) => store.AssessmentSlice.duration);
  const maxRecordDuration = constants?.mediaCheckDurationInSec;

  const videoProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.videoProctoring
  );

  const stepperFormActiveStep = useAppSelector(
    (state) => state.AssessmentSlice.preAssessment.stepperFormActiveStep
  );
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (selectedCamera && selectedMic) {
      startPreview();
    }
  }, [selectedCamera, selectedMic]);

  useEffect(() => {
    requestPermissions();

    // Unmount cleanup: stop media check stream and revoke blob URLs
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoURLRef.current) {
        URL.revokeObjectURL(videoURLRef.current);
      }
    };
  }, []);

  // Stops camera/mic stream from the pre-assessment media check
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const requestPermissions = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const cams = devices.filter((d) => d.kind === "videoinput");
      const mics = devices.filter((d) => d.kind === "audioinput");

      const camId = cams[0]?.deviceId;
      const micId = mics[0]?.deviceId;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: camId ? { deviceId: { exact: camId } } : true,
        audio: micId ? { deviceId: { exact: micId } } : true
      });

      streamRef.current = stream;

      setVideoDevices(cams);
      setAudioDevices(mics);

      setSelectedCamera(camId);
      setSelectedMic(micId);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;

        try {
          await videoRef.current.play();
        } catch (err) {
          console.warn("Play interrupted:", err);
        }
      }

      setPermissionError("");
      setisErr(false);
    } catch (err) {
      console.log(err);
      setisErr(true);
      setPermissionError(
        t("assessment.stepper.audioCheck.mediaPermissionDenied")
      );
    }
  };
  const startPreview = async () => {
    if (!selectedCamera || !selectedMic) return;

    try {
      const video = videoRef.current;

      if (video) {
        video.pause();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: selectedCamera } },
        audio: { deviceId: { exact: selectedMic } }
      });

      streamRef.current = stream;

      if (video) {
        video.srcObject = stream;
        video.muted = true;

        try {
          await video.play();
        } catch (err) {
          console.warn("Play interrupted:", err);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const startRecording = () => {
    if (!streamRef.current) {
      console.warn("Stream not ready");
      return;
    }

    setRecordingTime(0);
    setCurrentTime(0);

    const stream = streamRef.current;

    // Ensure preview is still live
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.play().catch((err) => {
        console.warn("Play interrupted on recording start:", err);
      });
    }

    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;

    const chunks: BlobPart[] = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    const startTime = Date.now();

    recordingIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setRecordingTime(elapsed);

      if (elapsed > maxRecordDuration) {
        stopRecording();
      }
    }, 250);

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/mp4" });

      // Revoke previous blob URL before creating a new one
      if (videoURL) {
        URL.revokeObjectURL(videoURL);
      }

      const url = URL.createObjectURL(blob);

      const tempVideo = document.createElement("video");
      tempVideo.src = url;

      tempVideo.onloadedmetadata = () => {
        videoURLRef.current = url;
        setVideoURL(url);

        // Switch to playback AFTER stop
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = url;
          videoRef.current.muted = false;
        }
      };
    };

    mediaRecorder.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const clickHandler = async () => {
    if (inResumeScreen) {
      if (videoProctoring && setActiveView) {
        setActiveView("systemCheck");
        return;
      }
      stopStream(); // Release camera/mic before starting assessment
      await startAssessmentHandler(inResumeScreen);
      return;
    }
    if (stepperFormActiveStep === 2) {
      if (tncEnabled || videoProctoring) {
        mediaRecorderRef.current?.stop();
        stopStream(); // Release camera/mic before navigating to next step
        if (videoProctoring)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(3));
        else dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(4));
      } else {
        stopStream(); // Release camera/mic before starting assessment
        await startAssessmentHandler(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
    setDuration(videoRef.current.duration || 0);
  };
  const handleRetake = () => {
    if (videoURL) {
      URL.revokeObjectURL(videoURL);
    }
    videoURLRef.current = "";
    setVideoURL("");
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setRecordingTime(0);

    if (videoRef.current && streamRef.current) {
      videoRef.current.src = "";
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch((err) => {
        console.warn("Play interrupted on retake:", err);
      });
    }
  };
  const startAssessmentHandler = async (inResumeScreen: boolean) => {
    try {
      if (!scheduleId) {
        throw {
          status: "",
          title: t("assessment.scheduleIdNotFound"),
          err: {},
          description: ""
        };
      }

      if (!inResumeScreen) {
        const res = await startAssessment({
          scheduleId: scheduleId,
          tncAccepted: false,
          tncAcceptedAt: null
        });

        if (res.error) {
          throw {
            status: "",
            title: t("assessment.systemCheck.failedToStartAssessment"),
            err: res.error,
            description: ""
          };
        }

        const ongoingId = res?.data?.data?.id;

        if (ongoingId) {
          dispatch(AssessmentSliceActions.mutateOnGoingId(ongoingId));
          const tabId = await generateUniqueId();
          if (tabId) {
            const body = {
              ongoingId: ongoingId,
              scheduleId: scheduleId,
              sessionId: tabId
            };
            const res1 = await updateTabId(body);
          }
        }
      }

      dispatch(AssessmentSliceActions.mutateShowWarningModal(false));
      mediaRecorderRef.current?.stop();
      dispatch(AssessmentSliceActions.mutateShowProctoringVideoPreview(true));
      //   startProctoring();
      dispatch(AssessmentSliceActions.mutateAssessmentHasStarted(true));
      dispatch(
        AssessmentSliceActions.mutateInitialProctoringVideoPreview(true)
      );
      //   if (browserProctoring || videoProctoring) requestFullScreen(dispatch);
    } catch (err) {
      console.error(err);
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play().catch((err) => {
        console.warn("Play interrupted:", err);
      });
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = percent * duration;
  };

  return (
    <div className="max-w-xl mx-auto text-center space-y-8">
      <div className="my-4">
        <InfoComponent
          textSize={"sm"}
          iconSize={5}
          iconVariant={"outline"}
          text={t("assessment.stepper.audioCheck.intro")}
          iconPosition={"start"}
        />
      </div>

      <div className="relative bg-black rounded-xl overflow-hidden group h-full">
        <video
          ref={videoRef}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setIsPlaying(false)}
          className="w-full h-72 object-cover"
        />

        {isRecording && (
          <div
            className="absolute top-2 left-2 transform
                    opacity-100 px-3 py-1.5 rounded-full bg-black/25
                    transition duration-300 z-10"
          >
            <div className="flex gap-2 cursor-pointer ">
              <div className="relative flex h-2.5 w-2.5 mt-1">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
              </div>
              <div className="text-white text-xs font-semibold">
                {t("assessment.subjective.recording")}
              </div>
            </div>
          </div>
        )}

        {videoURL && (
          <div
            className="absolute top-2 left-1/2 transform -translate-x-1/2
                    opacity-0 group-hover:opacity-100 
                    transition duration-300 z-10"
          >
            <div onClick={handleRetake} className="flex gap-2 cursor-pointer">
              <GetHeroIcon
                name="ArrowPathIcon"
                className="w-5 h-5 text-gray-200 hover:text-white"
                aria-hidden="true"
                variant="solid"
              />
              <div className="text-gray-200 hover:text-white text-sm">
                {t("assessment.subjective.retake")}
              </div>
            </div>
          </div>
        )}

        {videoURL && (
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center
                   group-hover:opacity-100 transition duration-300 z-10
                  pointer-events-none ${isPlaying ? "opacity-0" : "opacity-50"}`}
          >
            {/* Play/Pause Button */}
            <button
              onClick={togglePlay}
              className="pointer-events-auto bg-black/50 p-4 rounded-full flex items-center justify-center
                 hover:scale-110 transition-transform"
            >
              <GetHeroIcon
                name={isPlaying ? "PauseIcon" : "PlayIcon"}
                className="w-6 h-6 text-white"
                aria-hidden="true"
                variant="solid"
              />
            </button>
          </div>
        )}
        {/* Overlay Controls */}
        {videoURL && (
          <div
            className="absolute bottom-0 left-0 w-full 
                    bg-gradient-to-t from-black/80 to-transparent 
                    p-3 opacity-0 group-hover:opacity-100 
                    transition duration-300"
          >
            {/* Progress Bar */}
            <div
              onClick={handleSeek}
              className="w-full h-2 bg-gray-400/40 rounded cursor-pointer"
            >
              <div
                className="h-2 bg-primary rounded"
                style={{
                  width: duration ? `${(currentTime / duration) * 100}%` : "0%"
                }}
              />
            </div>

            {/* Timestamp display */}
            <div className="flex justify-between items-center text-xs text-white mt-2">
              <div className="flex items-center gap-3">
                <span>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>
        )}
        {isRecording && (
          <div
            className="absolute bottom-0 left-0 w-full p-3 
                            bg-gradient-to-t from-black/80 to-transparent 
                            opacity-100 transition duration-300"
          >
            <div className="flex items-center gap-2">
              {/* Progress Bar */}
              <div className="relative flex-1 h-2 bg-gray-400/40 rounded">
                {/* Current Progress */}
                <div
                  className="h-2 bg-primary rounded transition-all duration-200"
                  style={{
                    width: `${(recordingTime / maxRecordDuration) * 100}%`
                  }}
                />
              </div>
            </div>

            {/* Timestamp */}
            <div className="flex justify-between items-center text-xs text-white mt-2">
              <span>
                {formatTime(recordingTime)} / {formatTime(maxRecordDuration)}
              </span>
            </div>
          </div>
        )}
      </div>

      {permissionError && (
        <div className="text-red-600 text-sm">* {permissionError}</div>
      )}

      {/* Record buttons */}
      {!permissionError && (
        <div className="flex justify-center gap-4 mt-4">
          <Button
            variant="contained"
            label={
              isRecording
                ? t("assessment.stepper.audioCheck.stopRecording")
                : t("assessment.stepper.audioCheck.startRecording")
            }
            onClick={isRecording ? stopRecording : startRecording}
            iconName={isRecording ? "StopIcon" : "MicrophoneIcon"}
            buttonColor={isRecording ? "warning" : "secondary"}
          />
        </div>
      )}

      {/* Device selectors */}
      {videoDevices?.length > 0 && (
        <SelectCustom
          options={videoDevices.map((d) => ({
            id: d.deviceId,
            name: d.label || t("assessment.systemCheck.unnamedCamera"),
            value: d.deviceId
          }))}
          setterFunction={(e: any) => setSelectedCamera(e?.target?.value)}
        />
      )}

      {audioDevices?.length > 0 && (
        <SelectCustom
          options={audioDevices.map((d) => ({
            id: d.deviceId,
            name:
              d.label || t("assessment.stepper.audioCheck.unnamedMicrophone"),
            value: d.deviceId
          }))}
          setterFunction={(e: any) => setSelectedMic(e?.target?.value)}
        />
      )}

      <div className="flex items-center justify-between gap-5 mt-10 md:justify-end">
        {!inResumeScreen && (
          <Button
            type="button"
            variant="outlined"
            buttonColor="secondary"
            onClick={() => {
              if (setActiveStep) setActiveStep("previous");
            }}
            // disabled={processStatus === "Calibrating"}
          >
            {t("assessment.stepper.previousStep")}
          </Button>
        )}
        <Button
          type="button"
          variant="contained"
          buttonColor="primary"
          onClick={clickHandler}
          disable={isErr}
        >
          {tncEnabled || videoProctoring
            ? inResumeScreen
              ? t("assessment.stepper.nextStep")
              : t("assessment.stepper.nextStep")
            : inResumeScreen
              ? t("assessment.stepper.resumeAssessment")
              : t("assessment.stepper.startAssessment")}
        </Button>
      </div>
    </div>
  );
};

export default AudioVideoCheck;
